import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  requestPasswordReset,
  resetPasswordWithCode,
} from "../api/client";

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState("request"); // "request" | "reset"
  const [email, setEmail] = useState("");

  const [codeInput, setCodeInput] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();

    setError("");

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail) {
      setError("Please enter your account email.");
      return;
    }

    setBusy(true);

    try {
      await requestPasswordReset(cleanEmail);

      // OTP is sent by the backend to the user's email.
      setStep("reset");
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not send the verification code.";

      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    setError("");

    const cleanCode = codeInput.trim();

    if (!cleanCode || !newPassword || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    if (!/^\d{6}$/.test(cleanCode)) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);

    try {
      await resetPasswordWithCode(
        email.trim().toLowerCase(),
        cleanCode,
        newPassword
      );

      setDone(true);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not reset password.";

      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="oes-page container">
      <div className="row g-3 g-md-4 justify-content-center">
        <div className="col-12 col-sm-9 col-md-6 col-lg-4">
          <h2 className="h6 fw-bold mb-3">FORGOT PASSWORD</h2>

          <div className="oes-card p-4 p-md-5">
            <div className="text-center mb-4">
              <div
                className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center"
                style={{ width: 90, height: 90 }}
              >
                <i
                  className="fa-solid fa-key fs-1"
                  style={{ color: "var(--oes-navy)" }}
                />
              </div>
            </div>

            {done ? (
              <div className="text-center">
                <div className="alert alert-success small">
                  Your password has been reset successfully.
                </div>

                <button
                  className="btn btn-primary w-100 py-2"
                  onClick={() => navigate("/login")}
                >
                  BACK TO LOGIN
                </button>
              </div>
            ) : step === "request" ? (
              <form onSubmit={handleRequestCode} noValidate>
                <p className="small text-muted mb-3">
                  Enter the email linked to your account and we'll send a
                  verification code.
                </p>

                <div className="mb-4">
                  <input
                    type="email"
                    className="form-control py-2"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    autoComplete="email"
                  />
                </div>

                {error && (
                  <div className="alert alert-danger py-2 small">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2"
                  disabled={busy}
                >
                  {busy ? "Sending..." : "SEND CODE"}
                </button>

                <p className="text-center small text-muted mt-4 mb-0">
                  <Link
                    to="/login"
                    className="text-primary fw-semibold"
                  >
                    Back to Login
                  </Link>
                </p>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} noValidate>
                <div className="alert alert-info small">
                  A 6-digit verification code has been sent to your registered
                  email address. The code is valid for 10 minutes.
                </div>

                <div className="mb-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control py-2"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={codeInput}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);

                      setCodeInput(value);
                    }}
                    autoComplete="one-time-code"
                  />
                </div>

                <div className="mb-3">
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control py-2"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New Password"
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setShowPassword((value) => !value)
                      }
                      tabIndex={-1}
                    >
                      <i
                        className={`fa-solid ${
                          showPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control py-2"
                    value={confirmPassword}
                    onChange={(e) =>
                      setConfirmPassword(e.target.value)
                    }
                    placeholder="Confirm New Password"
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div className="alert alert-danger py-2 small">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2"
                  disabled={busy}
                >
                  {busy ? "Resetting..." : "RESET PASSWORD"}
                </button>

                <p className="text-center small text-muted mt-4 mb-0">
                  <Link
                    to="/login"
                    className="text-primary fw-semibold"
                  >
                    Back to Login
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}